/**
 * Suppliers Force-Directed Network Graph
 * Physics-based interactive visualization with HTML nodes
 */

class SupplierNetwork {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.nodesContainer = document.getElementById('suppliers-nodes');

        if (!this.canvas || !this.nodesContainer) return;

        this.ctx = this.canvas.getContext('2d', { alpha: true });
        this.animationId = null;

        this.attraction = 0.12;
        this.friction = 0.82;
        this.centerPull = 0.015;
        this.maxVelocity = 3.5;

        this.nodeRadius = 70;
        this.repulsion = 1000;
        this.minDistance = 240;

        this.hoveredNode = null;
        this.mousePos = { x: 0, y: 0 };
        this.isDragging = false;
        this.draggedNode = null;

        this.suppliers = [
            { name: 'Pirelli', logo: 'dist/assets/images/fornecedores/Pirelli.webp', featured: true, order: 1 },
            { name: 'Maggion', logo: 'dist/assets/images/fornecedores/Maggion.webp', featured: true, order: 2 },
            { name: 'Petronas', logo: 'dist/assets/images/fornecedores/petronas-logo.webp', featured: true, order: 3 },
            { name: 'Perfect', logo: 'dist/assets/images/fornecedores/Perfect.webp', medium: true },
            { name: 'Schadek', logo: 'dist/assets/images/fornecedores/SCHADEK.webp', medium: true },
            { name: 'Dayco', logo: 'dist/assets/images/fornecedores/DAYCO.webp', medium: true },
            { name: 'HippuFreias', logo: 'dist/assets/images/fornecedores/hipper-freios.webp', medium: true },
            { name: 'Wega Quality', logo: 'dist/assets/images/fornecedores/Wega.webp', medium: true },
            { name: 'Fortires', logo: 'dist/assets/images/fornecedores/Fortires.webp', medium: true },
            { name: 'Tigre', logo: 'dist/assets/images/fornecedores/Tigre.webp', medium: true },
            { name: 'Cobreq', logo: 'dist/assets/images/fornecedores/Cobreq.webp', medium: true },
            { name: 'Car-80', logo: 'dist/assets/images/fornecedores/Car-80.webp' },
            { name: 'OrbiQuimica', logo: 'dist/assets/images/fornecedores/ORBI_QUIMICA.webp' },
            { name: 'Riffel', logo: 'dist/assets/images/fornecedores/RIFFEL.webp' }
        ];

        this.nodes = [];
        this.links = [];
        this.nodeElements = [];

        this.init();
    }

    init() {
        this.setupCanvas();
        this.applyResponsiveRadius();
        this.createNodes();
        this.createLinks();
        this.createNodeElements();
        this.setupEventListeners();
        this.animate();
    }

    applyResponsiveRadius() {
        if (window.innerWidth <= 480) {
            this.nodeRadius = 20;
            this.repulsion = 500;
            this.minDistance = 120;
        } else if (window.innerWidth <= 768) {
            this.nodeRadius = 30;
            this.repulsion = 1000;
            this.minDistance = 200;
        } else if (window.innerWidth <= 1200) {
            this.nodeRadius = 40;
            this.repulsion = 1500;
            this.minDistance = 300;
        } else {
            this.nodeRadius = 45;
            this.repulsion = 2000;
            this.minDistance = 350;
        }
    }

    setupCanvas() {
        const wrapper = this.canvas.parentElement;
        const rect = wrapper.getBoundingClientRect();
        this.canvas.width = rect.width;
        const minHeight = window.innerWidth < 768 ? 800 : 600;
        this.canvas.height = Math.max(rect.height, minHeight);

        window.addEventListener('resize', () => this.onResize());
    }

    onResize() {
        const wrapper = this.canvas.parentElement;
        const rect = wrapper.getBoundingClientRect();
        this.canvas.width = rect.width;
        const minHeight = window.innerWidth < 768 ? 800 : 600;
        this.canvas.height = Math.max(rect.height, minHeight);
        this.applyResponsiveRadius();
        this.resizeNodes();
    }

    resizeNodes() {
        this.nodeElements.forEach(({ node, element }) => {
            const size = this.nodeRadius * 2 * node.radiusModifier;
            element.style.width = size + 'px';
            element.style.height = size + 'px';
        });
    }

    createNodes() {
        const centerX = this.canvas.width / 2;
        const centerOffset = window.innerWidth < 768 ? 120 : 80;
        const centerY = (this.canvas.height / 2) - centerOffset;

        this.suppliers.forEach((supplier, index) => {
            let x, y;
            if (supplier.featured) {
                const angle = (supplier.order / 3) * Math.PI * 2;
                x = centerX + Math.cos(angle) * 100;
                y = centerY + Math.sin(angle) * 100;
            } else {
                const angle = (index / this.suppliers.length) * Math.PI * 2;
                const dist = window.innerWidth < 768 ? 220 : 350;
                x = centerX + Math.cos(angle) * dist;
                y = centerY + Math.sin(angle) * dist;
            }

            this.nodes.push({
                ...supplier,
                id: index,
                x: x,
                y: y,
                vx: 0,
                vy: 0,
                fx: 0,
                fy: 0,
                mass: supplier.featured ? 4 : (supplier.medium ? 2 : 1), // Immense mass diff
                pinned: false,
                radiusModifier: supplier.featured ? 1.8 : (supplier.medium ? 1.4 : 1.1) // Boost featured items to stand out without covering everything
            });
        });
    }

    createLinks() {
        const featured = this.nodes.filter(n => n.featured);
        for (let i = 0; i < featured.length; i++) {
            for (let j = i + 1; j < featured.length; j++) {
                this.links.push({
                    source: featured[i],
                    target: featured[j],
                    distance: this.minDistance * 1.2, // Provide more space between featured nodes
                    strength: 0.05,
                    active: true
                });
            }
        }

        // Standard orbital links
        for (let i = 0; i < this.nodes.length; i++) {
            const nextIndex = (i + 1) % this.nodes.length;
            this.links.push({
                source: this.nodes[i],
                target: this.nodes[nextIndex],
                distance: this.minDistance,
                strength: 0.03,
                active: false
            });
        }
    }

    createNodeElements() {
        this.nodes.forEach((node) => {
            const nodeEl = document.createElement('div');
            nodeEl.className = 'supplier-node' + (node.featured ? ' featured' : '') + (node.medium ? ' medium' : '');
            const size = this.nodeRadius * 2 * node.radiusModifier;
            nodeEl.style.width = size + 'px';
            nodeEl.style.height = size + 'px';

            nodeEl.innerHTML = `
                <div class="supplier-node-inner">
                    <img src="${node.logo}" alt="${node.name}" loading="lazy" draggable="false">
                </div>
                <div class="supplier-label">${node.name}</div>
            `;

            nodeEl.addEventListener('mousedown', (e) => this.onNodeMouseDown(e, node));
            nodeEl.addEventListener('touchstart', (e) => this.onNodeTouchStart(e, node), { passive: false });

            this.nodesContainer.appendChild(nodeEl);
            this.nodeElements.push({ node, element: nodeEl });
        });
    }

    updateNodePositions() {
        this.nodeElements.forEach(({ node, element }) => {
            const currentRadius = this.nodeRadius * node.radiusModifier;
            const x = node.x - currentRadius;
            const y = node.y - currentRadius;
            element.style.transform = `translate(${x}px, ${y}px)`;
        });
    }

    setupEventListeners() {
        const wrapper = this.canvas.parentElement;
        wrapper.addEventListener('mousemove', (e) => this.onMouseMove(e));
        wrapper.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
        wrapper.addEventListener('mouseleave', () => this.onMouseLeave());

        document.addEventListener('mouseup', () => this.onMouseUp());
        document.addEventListener('touchend', () => this.onMouseUp(), { passive: false });

        // Global drag prevention
        this.nodesContainer.addEventListener('dragstart', (e) => e.preventDefault());
        this.nodesContainer.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    onMouseMove(e) {
        const rect = this.canvas.getBoundingClientRect();
        this.mousePos.x = e.clientX - rect.left;
        this.mousePos.y = e.clientY - rect.top;

        if (this.isDragging && this.draggedNode) {
            this.draggedNode.x = this.mousePos.x;
            this.draggedNode.y = this.mousePos.y;
            this.draggedNode.vx = 0;
            this.draggedNode.vy = 0;
        }
    }

    onTouchMove(e) {
        if (e.touches && e.touches.length > 0) {
            const rect = this.canvas.getBoundingClientRect();
            this.mousePos.x = e.touches[0].clientX - rect.left;
            this.mousePos.y = e.touches[0].clientY - rect.top;

            if (this.isDragging && this.draggedNode) {
                this.draggedNode.x = this.mousePos.x;
                this.draggedNode.y = this.mousePos.y;
                this.draggedNode.vx = 0;
                this.draggedNode.vy = 0;
                e.preventDefault();
            }
        }
    }

    onMouseLeave() {
        if (!this.isDragging) {
            this.hoveredNode = null;
        }
    }

    onNodeMouseDown(e, node) {
        this.isDragging = true;
        this.draggedNode = node;
        e.preventDefault();
    }

    onNodeTouchStart(e, node) {
        if (e.touches && e.touches.length > 0) {
            this.isDragging = true;
            this.draggedNode = node;
            const rect = this.canvas.getBoundingClientRect();
            this.mousePos.x = e.touches[0].clientX - rect.left;
            this.mousePos.y = e.touches[0].clientY - rect.top;
            e.preventDefault();
        }
    }

    onMouseUp() {
        this.isDragging = false;
        this.draggedNode = null;
    }

    update() {
        this.nodes.forEach(node => {
            node.fx = 0;
            node.fy = 0;
        });

        // 1. REPULSION: Prevent overlap using inverse square law + buffer
        for (let i = 0; i < this.nodes.length; i++) {
            for (let j = i + 1; j < this.nodes.length; j++) {
                const nodeA = this.nodes[i];
                const nodeB = this.nodes[j];
                const dx = nodeB.x - nodeA.x;
                const dy = nodeB.y - nodeA.y;
                const distance = Math.sqrt(dx * dx + dy * dy) + 1;

                // Effective repulsion radius depends on node modifiers
                const combinedRadius = (this.nodeRadius * nodeA.radiusModifier) + (this.nodeRadius * nodeB.radiusModifier);
                const softMinDist = combinedRadius * 1.5;

                let force = 0;
                if (distance < softMinDist) {
                    force = this.repulsion * (softMinDist / (distance * distance));
                }

                const fx = (dx / distance) * force;
                const fy = (dy / distance) * force;

                nodeA.fx -= fx;
                nodeA.fy -= fy;
                nodeB.fx += fx;
                nodeB.fy += fy;
            }
        }

        // 2. LINK FORCES: Keep related nodes together
        this.links.forEach(link => {
            const dx = link.target.x - link.source.x;
            const dy = link.target.y - link.source.y;
            const distance = Math.sqrt(dx * dx + dy * dy) + 1;
            const force = (distance - link.distance) * link.strength;

            const fx = (dx / distance) * force;
            const fy = (dy / distance) * force;

            link.source.fx += fx;
            link.source.fy += fy;
            link.target.fx -= fx;
            link.target.fy -= fy;
        });

        // 3. CENTER & FEATURED PULL
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;

        this.nodes.forEach(node => {
            const dx = centerX - node.x;
            const dy = centerY - node.y;

            // Featured nodes pull harder to center
            const pullStrength = node.featured ? this.centerPull * 2 : (node.medium ? this.centerPull * 1.5 : this.centerPull);
            node.fx += dx * pullStrength;
            node.fy += dy * pullStrength;
        });

        // 4. PHYSICS UPDATE: Velocity and Friction
        this.nodes.forEach(node => {
            if (this.draggedNode === node) return;

            node.vx += node.fx / node.mass;
            node.vy += node.fy / node.mass;

            node.vx *= this.friction;
            node.vy *= this.friction;

            const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
            if (speed > this.maxVelocity) {
                node.vx = (node.vx / speed) * this.maxVelocity;
                node.vy = (node.vy / speed) * this.maxVelocity;
            }

            node.x += node.vx;
            node.y += node.vy;
        });

        // 5. HARD COLLISION RESOLUTION: Rigid boundaries to prevent ANY overlap
        const iterations = 5; // High rigidity
        for (let iter = 0; iter < iterations; iter++) {
            for (let i = 0; i < this.nodes.length; i++) {
                for (let j = i + 1; j < this.nodes.length; j++) {
                    const nodeA = this.nodes[i];
                    const nodeB = this.nodes[j];
                    const dx = nodeB.x - nodeA.x;
                    const dy = nodeB.y - nodeA.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    const minAllowedDist = (this.nodeRadius * nodeA.radiusModifier) + (this.nodeRadius * nodeB.radiusModifier) + 10;

                    if (distance > 0 && distance < minAllowedDist) {
                        const overlap = minAllowedDist - distance;
                        const nx = dx / distance;
                        const ny = dy / distance;

                        // Push away relative to mass
                        const totalMass = nodeA.mass + nodeB.mass;
                        const ratioA = nodeB.mass / totalMass;
                        const ratioB = nodeA.mass / totalMass;

                        nodeA.x -= nx * (overlap * ratioA);
                        nodeA.y -= ny * (overlap * ratioA);
                        nodeB.x += nx * (overlap * ratioB);
                        nodeB.y += ny * (overlap * ratioB);
                    }
                }

                // Canvas boundaries
                const node = this.nodes[i];
                const r = this.nodeRadius * node.radiusModifier;
                if (node.x < r) node.x = r;
                if (node.x > this.canvas.width - r) node.x = this.canvas.width - r;
                if (node.y < r) node.y = r;
                if (node.y > this.canvas.height - r) node.y = this.canvas.height - r;
            }
        }
    }

    draw() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        this.nodes.forEach(node => {
            if (node.featured) {
                const gradient = this.ctx.createRadialGradient(
                    node.x, node.y, 0,
                    node.x, node.y, this.nodeRadius * node.radiusModifier * 1.5
                );
                gradient.addColorStop(0, 'rgba(232, 80, 10, 0.1)');
                gradient.addColorStop(1, 'rgba(232, 80, 10, 0)');
                this.ctx.fillStyle = gradient;
                this.ctx.beginPath();
                this.ctx.arc(node.x, node.y, this.nodeRadius * node.radiusModifier * 1.5, 0, Math.PI * 2);
                this.ctx.fill();
            }
        });

        // Draw links
        this.links.forEach(link => {
            this.ctx.strokeStyle = link.active ? 'rgba(232, 80, 10, 0.2)' : 'rgba(255, 255, 255, 0.05)';
            this.ctx.lineWidth = link.active ? 2 : 1;
            this.ctx.beginPath();
            this.ctx.moveTo(link.source.x, link.source.y);
            this.ctx.lineTo(link.target.x, link.target.y);
            this.ctx.stroke();
        });
    }

    animate() {
        this.update();
        this.draw();
        this.updateNodePositions();
        this.animationId = requestAnimationFrame(() => this.animate());
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new SupplierNetwork('suppliers-canvas');
});